import {
  EContentOfPairedStackContainer,
  IBayPattern,
  ITierPattern,
  PairedStackContainer,
  SmartPairedBayBlock,
  SmartUnpairedBayBlock,
  TIContainerInformationExtendedValid,
  UnpairedStackContainer,
} from "@tedivo/tedivo-bay-grid-pure";

import { TGetContainersFilterOutput } from "./TGetContainersFilterOutput";

export function getSideContainers(
  side: "port" | "starboard",
): (smartBay: SmartUnpairedBayBlock | SmartPairedBayBlock) => TGetContainersFilterOutput {
  return function getSideContainersFn(
    smartBay: SmartUnpairedBayBlock | SmartPairedBayBlock,
  ): TGetContainersFilterOutput {
    const [fwdBay] = smartBay.tBaysStr.split("-") as [IBayPattern];

    const tiersContent = new Map<
      ITierPattern,
      | {
          isFullyCovered: boolean;
          maxContent: EContentOfPairedStackContainer;
          maxSize: number;
          cs: (UnpairedStackContainer | PairedStackContainer)[];
        }
      | undefined
    >();

    const { gridContent, allStacksKeys, allTiersKeys } = smartBay.getGridContent();

    const stacksKeysOrdered =
      side === "starboard" ? allStacksKeys.slice().reverse() : allStacksKeys.slice();

    for (const tierKey of allTiersKeys) {
      for (const stackKey of stacksKeysOrdered) {
        const currContent = gridContent[stackKey]?.[tierKey];
        if (!currContent || currContent.maxSize === undefined) continue;

        const existingTierContent = tiersContent.get(tierKey);

        if (!existingTierContent) {
          /** If the tier is not in the map, add it */
          tiersContent.set(tierKey, {
            isFullyCovered:
              smartBay.type === "unpaired"
                ? currContent.content !== EContentOfPairedStackContainer.NONE
                : currContent.content === EContentOfPairedStackContainer.ONE40 ||
                  currContent.content === EContentOfPairedStackContainer.TWO20,
            maxContent: currContent.content,
            maxSize: currContent.maxSize,
            cs: [currContent],
          });
          continue;
        } else {
          /** If the smartTier has already a 40x, continue */
          if (
            (existingTierContent.maxContent === EContentOfPairedStackContainer.ONE40 ||
              existingTierContent.maxContent === EContentOfPairedStackContainer.TWO20) &&
            existingTierContent.maxSize >= currContent.maxSize
          )
            continue;

          // Add it following the rules:
          // It can be ONE40, ONE20_FWD, ONE20_AFT or TWO20.
          switch (currContent.content) {
            case EContentOfPairedStackContainer.ONE40:
              // If it is ONE40 here, it has a bigger size than the previous one (initial if), so we add it
              existingTierContent.cs.push(currContent);
              existingTierContent.maxContent = EContentOfPairedStackContainer.ONE40;
              existingTierContent.maxSize = currContent.maxSize;
              tiersContent.set(tierKey, existingTierContent);
              break;
            case EContentOfPairedStackContainer.TWO20:
              // If it is TWO20 here, means a whole block of 20ft containers,
              // The previous content can only be ONE20_FWD or ONE20_AFT or smaller TWO20, so we add it
              existingTierContent.cs.push(currContent);
              existingTierContent.maxContent = EContentOfPairedStackContainer.TWO20;
              existingTierContent.maxSize = currContent.maxSize;
              tiersContent.set(tierKey, existingTierContent);
              break;
            case EContentOfPairedStackContainer.ONE20_FWD:
              // If it is ONE20_FWD here, check if the previous was the same and smaller
              if (
                existingTierContent.maxContent === currContent.content &&
                existingTierContent.maxSize <= currContent.maxSize
              ) {
                tiersContent.set(tierKey, {
                  isFullyCovered: false,
                  maxContent: EContentOfPairedStackContainer.ONE20_FWD,
                  maxSize: currContent.maxSize,
                  cs: [currContent],
                });
              } else if (
                existingTierContent.maxContent === EContentOfPairedStackContainer.ONE20_AFT
              ) {
                // If the previous was ONE20_AFT, we need to create a new PairedStackContainer with the two 20s
                const newContent = new PairedStackContainer(tierKey, Number(fwdBay));
                existingTierContent.cs.push(newContent);
                existingTierContent.maxContent = EContentOfPairedStackContainer.TWO20;
                tiersContent.set(tierKey, existingTierContent);
              }
              break;
            case EContentOfPairedStackContainer.ONE20_AFT:
              if (
                existingTierContent.maxContent === currContent.content &&
                existingTierContent.maxSize <= currContent.maxSize
              ) {
                tiersContent.set(tierKey, {
                  isFullyCovered: false,
                  maxContent: EContentOfPairedStackContainer.ONE20_AFT,
                  maxSize: currContent.maxSize,
                  cs: [currContent],
                });
              } else if (
                existingTierContent.maxContent === EContentOfPairedStackContainer.ONE20_FWD
              ) {
                // If the previous was ONE20_FWD, we need to create a new PairedStackContainer with the two 20s
                const newContent = new PairedStackContainer(tierKey, Number(fwdBay));
                existingTierContent.cs.push(newContent);
                existingTierContent.maxContent = EContentOfPairedStackContainer.TWO20;
                tiersContent.set(tierKey, existingTierContent);
              }
              break;
          }
        }
      }
    }

    const containers: TIContainerInformationExtendedValid[] = [];
    tiersContent.forEach((tc) => {
      if (tc)
        containers.push(
          ...(tc.cs.flatMap((c) => c.containers) as TIContainerInformationExtendedValid[]),
        );
    });

    return {
      containers,
      tBaysStr: smartBay.tBaysStr,
      level: smartBay.level,
    };
  };
}
